import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    FluentProvider,
    Input,
    Label,
    makeStyles,
    mergeClasses,
    useId,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import * as React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useTakeSnapshotDialogContext } from '../../../hooks/context/dialogs';
import { useCurrentFluent2Theme } from '../../../hooks/styling';
import { useStackStyles } from '../../../themes/styles/flexbox-styles';

export interface TakeSnapshotDialogProps {
    onDismiss: () => void;
    onTakeSnapshotSubmitted: () => void;
    open?: boolean;
}

const messages = defineMessages({
    takeSnapshotButtonAriaLabel: {
        id: 'TakeSnapshotDialog_Button_AriaLabel',
        defaultMessage: 'Take Snapshot',
        description: 'Aria label for the submit button on the Take Snapshot dialog',
    },
    cancelButtonAriaLabel: {
        id: 'TakeSnapshotDialog_CancelButton_AriaLabel',
        defaultMessage: 'Cancel',
        description: 'Aria label for the cancel button on the Take Snapshot dialog.',
    },
    closeButtonAriaLabel: {
        id: 'TakeSnapshotDialog_CloseButton_Text',
        defaultMessage: 'Close',
        description: 'Aria label for the "x" button in the top corner of the TakeSnapshot dialog',
    },
});

/**
 * Styles
 */
const dialogWidth = '520px';
const dialogStyle: React.CSSProperties = {
    width: dialogWidth,
};

const useDialogStackStyles = makeStyles({
    root: {
        fontWeight: 'normal',
        gap: '18px',
        padding: '0px 0px 30px 0px',
    },
});

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        width: '100%',
        maxWidth: '400px',
    },
});

/**
 * End Styles
 */

const TakeSnapshotDialog: React.FC<TakeSnapshotDialogProps> = (props: TakeSnapshotDialogProps) => {
    const { onDismiss, onTakeSnapshotSubmitted, open } = props;
    const snapshotId = useId('input');
    const [value] = React.useState(
        new Date()
            .toLocaleString('en-US', {
                month: 'numeric',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
            })
            .replace(',', ' at')
    );

    // Theming hooks
    const theme = useCurrentFluent2Theme();

    // Intl hooks
    const { formatMessage } = useIntl();

    // Style hooks
    const stackStyles = useStackStyles();
    const dialogStackStyles = useDialogStackStyles();
    const styles = useStyles();

    const onSubmit = React.useCallback(() => {
        onTakeSnapshotSubmitted();
        onDismiss();
    }, [onTakeSnapshotSubmitted, onDismiss]);

    return (
        <FluentProvider theme={theme}>
            <Dialog modalType="alert" open={open} onOpenChange={onDismiss}>
                <DialogSurface style={dialogStyle}>
                    <DialogBody>
                        <DialogTitle
                            action={
                                <DialogTrigger action="close">
                                    <Button
                                        appearance="subtle"
                                        aria-label={formatMessage(messages.closeButtonAriaLabel)}
                                        icon={<Dismiss24Regular />}
                                    />
                                </DialogTrigger>
                            }
                        >
                            <FormattedMessage
                                id="TakeSnapshotDialog_Title"
                                defaultMessage="Take snapshot?"
                                description="The title for the dialog describing the Take Snapshot."
                            />
                        </DialogTitle>
                        <DialogContent>
                            <div className={mergeClasses(stackStyles.root, dialogStackStyles.root)}>
                                <div className={stackStyles.item}>
                                    <FormattedMessage
                                        id="TakeSnapshotDialog_Description"
                                        defaultMessage="Taking a snapshot will enable you to receive the current state of your dev box as a backup."
                                        description="Take Snapshot dialog description."
                                    />
                                </div>
                                <div className={styles.root}>
                                    <Label htmlFor={snapshotId}>
                                        <FormattedMessage
                                            id="TakeSnapshotDialog_SnapshotName_Label"
                                            defaultMessage="Snapshot name"
                                            description="Label for the snapshot name input field in the Take Snapshot dialog"
                                        />
                                    </Label>
                                    <Input value={value} id={snapshotId} readOnly />
                                </div>
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                appearance="primary"
                                aria-label={formatMessage(messages.takeSnapshotButtonAriaLabel)}
                                onClick={onSubmit}
                            >
                                <FormattedMessage
                                    id="TakeSnapshotDialog_SubmitButton_Label"
                                    defaultMessage="Take snapshot"
                                    description="Label for the submit button on the take snapshot dialog."
                                />
                            </Button>
                            <DialogTrigger>
                                <Button
                                    appearance="secondary"
                                    aria-label={formatMessage(messages.cancelButtonAriaLabel)}
                                >
                                    <FormattedMessage
                                        id="TakeSnapshotDialog_CancelButton_Label"
                                        defaultMessage="Cancel"
                                        description="Label for the cancel button on the Take Snapshot dialog."
                                    />
                                </Button>
                            </DialogTrigger>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </FluentProvider>
    );
};

export const TakeSnapshotDialogContextWrapper: React.FC = () => {
    // Context hooks
    const { isOpen, closeSurface, properties } = useTakeSnapshotDialogContext();

    if (!isOpen) {
        return <></>;
    }

    return <TakeSnapshotDialog {...properties} open={isOpen} onDismiss={closeSurface} />;
};
